<template>
  <div class="show_all_content_wrapper">
    <main>
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.return_number')"
                v-model.trim="filterOptions.return_number"
              />

              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.order_number')"
                v-model.trim="filterOptions.order_number"
              />

              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.customer_name')"
                v-model.trim="filterOptions.customer_name"
              />

              <base-select-input
                col="4"
                :optionsList="statusOptions"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.status"
              />

              <base-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from_date')"
                v-model="filterOptions.date_from"
              />

              <base-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to_date')"
                v-model="filterOptions.date_to"
              />
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.return_requests") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>

        <template v-slot:[`item.id`]="{ item, index }">
          <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.id">-</p>
          <p v-else>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>

        <template v-slot:[`item.customer_name`]="{ item }">
          {{ item?.user?.name || "-" }}
        </template>

        <template v-slot:[`item.status`]="{ item }">
          {{ getStatusLabel(item?.status) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
      </v-data-table>
    </main>

    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllReturnRequests",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    statusOptions() {
      return [
        { id: null, name: this.$t("STATUS.all"), value: null },
        {
          id: 1,
          name: this.$t("STATUS.pending_review"),
          value: "pending_review",
        },
        { id: 2, name: this.$t("STATUS.accepted"), value: "accepted" },
        { id: 3, name: this.$t("STATUS.on_the_way"), value: "on_the_way" },
        { id: 4, name: this.$t("STATUS.received"), value: "received" },
        { id: 5, name: this.$t("STATUS.completed"), value: "completed" },
        { id: 6, name: this.$t("STATUS.rejected"), value: "rejected" },
        { id: 7, name: this.$t("STATUS.cancelled"), value: "cancelled" },
      ];
    },
  },

  data() {
    return {
      loading: false,
      isWaitingRequest: false,

      filterFormIsActive: false,
      filterOptions: {
        status: null,
        return_number: null,
        order_number: null,
        customer_name: null,
        date_from: null,
        date_to: null,
      },

      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.return_number"),
          value: "return_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.order_number"),
          value: "order_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.customer_name"),
          value: "customer_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],
      tableRows: [],

      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    translateStatus(status) {
      if (!status) return "-";

      const normalizedStatus = status === "new" ? "pending_review" : status;
      const translationKey = `STATUS.${normalizedStatus}`;

      return this.$te(translationKey) ? this.$t(translationKey) : status;
    },

    getStatusLabel(status) {
      return this.translateStatus(status);
    },

    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/return-requests/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.status = null;
      this.filterOptions.return_number = null;
      this.filterOptions.order_number = null;
      this.filterOptions.customer_name = null;
      this.filterOptions.date_from = null;
      this.filterOptions.date_to = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/return-requests/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;

      try {
        const response = await this.$axios({
          method: "GET",
          url: "return-requests",
          params: {
            page: this.paginations.current_page,
            per_page: this.paginations.items_per_page,
            "filter[status]":
              this.filterOptions.status?.value === "new"
                ? "pending_review"
                : this.filterOptions.status?.value,
            "filter[return_number]": this.filterOptions.return_number,
            "filter[order_number]": this.filterOptions.order_number,
            "filter[customer_name]": this.filterOptions.customer_name,
            "filter[date_from]": this.filterOptions.date_from,
            "filter[date_to]": this.filterOptions.date_to,
          },
        });

        const payload = response?.data?.data ?? response?.data ?? {};
        this.tableRows = payload?.data ?? payload?.items ?? [];
        this.paginations.current_page =
          payload?.meta?.current_page ?? this.paginations.current_page;
        this.paginations.last_page = payload?.meta?.last_page ?? 1;
        this.paginations.items_per_page = payload?.meta?.per_page ?? 10;
      } catch (error) {
        console.log(error?.response?.data?.message);
      } finally {
        this.loading = false;
      }
    },

    showItem(item) {
      this.$router.push({ path: `/return-requests/show/${item.id}` });
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.setTableRows();
  },
};
</script>
